import { useCallback, useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import type { CurrencyParam, PlansDuration } from '~/pages/PricePage/types';

const defaultDuration: PlansDuration = 'annual';
const defaultCurrency: CurrencyParam = 'gbp';

const durationSearchParamKey = 'duration';
const currencySearchParamKey = 'currency';

function usePriceSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract parameters with correct typing
  const durationParam =
    (searchParams.get(durationSearchParamKey) as PlansDuration) || defaultDuration;

  const currencyParam =
    (searchParams.get(currencySearchParamKey) as CurrencyParam) || defaultCurrency;

  // Create a single function to update multiple search parameters
  const updateSearchParams = useCallback(
    (newParams: Record<string, string>) => {
      const updatedParams = new URLSearchParams(searchParams);

      // Merge new params into existing ones
      Object.entries(newParams).forEach(([key, value]) => {
        if (value) {
          updatedParams.set(key, value);
        }
      });

      setSearchParams(updatedParams);
    },
    [searchParams, setSearchParams],
  );

  // Normalize query parameters and set defaults if necessary
  useEffect(() => {
    const validDurations: PlansDuration[] = ['annual', 'monthly'];
    const validCurrencies: CurrencyParam[] = ['gbp', 'usd'];

    const updates: Record<string, string> = {};

    // Normalize `duration` param
    if (!validDurations.includes(durationParam)) {
      updates[durationSearchParamKey] = defaultDuration;
    }

    if (!validCurrencies.includes(currencyParam)) {
      updates[currencySearchParamKey] = defaultCurrency;
    }

    // Update search params only if there are any changes
    if (Object.keys(updates).length > 0) {
      updateSearchParams(updates);
    }
  }, [durationParam, updateSearchParams, currencyParam]);

  function setDurationParam(duration: PlansDuration) {
    updateSearchParams({ [durationSearchParamKey]: duration });
  }

  function setCurrencyParam(currency: CurrencyParam) {
    updateSearchParams({ [currencySearchParamKey]: currency });
  }

  return {
    currencyParam,
    durationParam,
    setCurrencyParam,
    setDurationParam,
  };
}

export default usePriceSearchParams;
